import { getPageRoutes, listPuckPages } from "@/api/cms-puck/api";
import { DecoupagePickerField } from "@/components/cms-puck/fields/DecoupagePickerField";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import {
  catalogDecoupageLinkKey,
  DEFAULT_BUTTON_LINK,
  DEFAULT_CATALOG_DECOUPAGE_LINK,
  DEFAULT_OPTIONAL_LINK,
  getSystemRoutes,
  normalizeButtonLink,
  type ButtonLink,
  type ButtonLinkTarget,
  type CatalogDecoupageLink,
} from "@/lib/cms-puck/blocks/button";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useId } from "react";

function resolveLinkFieldValue(
  value: ButtonLink | undefined,
  allowNone: boolean,
): ButtonLink {
  const missingTarget =
    value == null ||
    (typeof value === "object" &&
      (value as Partial<ButtonLink>).targetType == null);
  if (missingTarget) {
    return normalizeButtonLink(
      allowNone ? { ...DEFAULT_OPTIONAL_LINK } : { ...DEFAULT_BUTTON_LINK },
    );
  }
  return normalizeButtonLink(value);
}

/**
 * Champ Puck custom : cible d'un lien (page interne, listing découpage, page
 * spéciale ou URL) + « ouvrir dans un nouvel onglet ».
 */
export function LinkTargetField({
  value,
  onChange,
  allowNone = false,
  allowedTargets,
  fixedCatalogDecoupage,
}: {
  value: ButtonLink | undefined;
  onChange: (value: ButtonLink) => void;
  /** Ajoute l'option « Aucun lien » (lien optionnel : titre, image). */
  allowNone?: boolean;
  /**
   * Sous-ensemble de cibles (ex. PAGE + CUSTOM pour Découpages).
   * Sans valeur = toutes les cibles habituelles.
   */
  allowedTargets?: ButtonLinkTarget[];
  /**
   * Si fourni, « Listing découpage » est verrouillé sur ce découpage
   * (URL classique `/{slug}`) — pas de picker.
   */
  fixedCatalogDecoupage?: CatalogDecoupageLink;
}) {
  const cmsNew = useCmsNew();
  const uid = useId();
  // Lien optionnel (titre, texte, image…) : sans valeur / sans targetType →
  // « Aucun lien », pas « Lien externe » (défaut de normalizeButtonLink).
  const link = resolveLinkFieldValue(value, allowNone);
  const allow = (t: ButtonLinkTarget) =>
    !allowedTargets || allowedTargets.includes(t);
  const { instanceData } = useAuth();
  const { defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  const frontUrl = (instanceData?.frontUrl ?? "").replace(/\/$/, "");

  const { data: pages = [] } = useQuery({
    queryKey: [
      "cms-puck",
      "pages",
      {
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
      },
    ],
    queryFn: () =>
      listPuckPages({
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
      }),
  });

  const { data: pageRoutes = [] } = useQuery({
    queryKey: ["cms-puck", "page-routes", link.pageId],
    queryFn: () => getPageRoutes(link.pageId!),
    enabled: link.targetType === "PAGE" && !!link.pageId,
  });

  const update = (patch: Partial<ButtonLink>) =>
    onChange({ ...link, ...patch });

  const previewUrl = (() => {
    if (link.targetType === "SYSTEM" && link.systemKey) {
      const route = getSystemRoutes().find((r) => r.key === link.systemKey);
      if (!route) return null;
      return `${frontUrl}/${route.path}`;
    }
    if (link.targetType === "PAGE" && link.pageId) {
      const route =
        pageRoutes.find((r) => r.lang === defaultWebsiteLanguage) ??
        pageRoutes[0];
      const slug = route?.slug ?? "";
      return slug ? `${frontUrl}/${slug}` : frontUrl || "/";
    }
    if (link.targetType === "CUSTOM" && link.url?.trim()) {
      const url = link.url.trim();
      if (/^https?:\/\//i.test(url)) return url;
      return frontUrl
        ? `${frontUrl}${url.startsWith("/") ? url : `/${url}`}`
        : url;
    }
    return null;
  })();
  const catalogDecoupage: CatalogDecoupageLink =
    link.catalogDecoupage ?? DEFAULT_CATALOG_DECOUPAGE_LINK;

  const updateCatalogDecoupage = (patch: Partial<CatalogDecoupageLink>) =>
    update({
      catalogDecoupage: { ...catalogDecoupage, ...patch },
    });

  const handleTargetTypeChange = (v: ButtonLinkTarget) => {
    if (v === link.targetType) return;
    if (v === "NONE") {
      onChange({
        targetType: "NONE",
        pageId: null,
        url: null,
        systemKey: null,
        catalogDecoupage: null,
        openInNew: false,
      });
      return;
    }
    if (v === "CATALOG_DECOUPAGE") {
      onChange({
        targetType: "CATALOG_DECOUPAGE",
        pageId: null,
        url: null,
        systemKey: null,
        catalogDecoupage:
          fixedCatalogDecoupage ??
          link.catalogDecoupage ?? {
            ...DEFAULT_CATALOG_DECOUPAGE_LINK,
          },
        openInNew: link.openInNew,
      });
      return;
    }
    if (v === "PAGE") {
      onChange({
        targetType: "PAGE",
        pageId: link.pageId,
        url: null,
        systemKey: null,
        catalogDecoupage: null,
        openInNew: link.openInNew,
      });
      return;
    }
    if (v === "CUSTOM") {
      onChange({
        targetType: "CUSTOM",
        pageId: null,
        systemKey: null,
        catalogDecoupage: null,
        // "" (pas "#") : "#" est le placeholder bouton et serait re-coercé en NONE.
        url: link.url?.trim() && link.url !== "#" ? link.url : "",
        openInNew: link.openInNew,
      });
      return;
    }
    onChange({
      ...link,
      targetType: v,
    });
  };

  const targetOptions: {
    value: ButtonLinkTarget;
    label: string;
  }[] = [
    ...(allowNone && allow("NONE")
      ? [
          {
            value: "NONE" as const,
            label: cmsNew(
              "cms.new.fields.linkTarget.targetNone",
              "Aucun lien",
            ),
          },
        ]
      : []),
    ...(allow("CATALOG_DECOUPAGE")
      ? [
          {
            value: "CATALOG_DECOUPAGE" as const,
            label: fixedCatalogDecoupage
              ? cmsNew(
                  "cms.new.fields.linkTarget.targetDecoupageClassic",
                  "Listing classique (/nom-du-cr/nom-du-découpage)",
                )
              : cmsNew(
                  "cms.new.fields.linkTarget.targetDecoupage",
                  "Listing d'un découpage",
                ),
          },
        ]
      : []),
    ...(allow("PAGE")
      ? [
          {
            value: "PAGE" as const,
            label: cmsNew(
              "cms.new.fields.linkTarget.targetPage",
              "Page du site",
            ),
          },
        ]
      : []),
    ...(allow("SYSTEM")
      ? [
          {
            value: "SYSTEM" as const,
            label: cmsNew(
              "cms.new.fields.linkTarget.targetSystem",
              "Page spéciale du site",
            ),
          },
        ]
      : []),
    ...(allow("CUSTOM")
      ? [
          {
            value: "CUSTOM" as const,
            label: cmsNew(
              "cms.new.fields.linkTarget.targetCustom",
              "Lien externe / personnalisé",
            ),
          },
        ]
      : []),
  ];

  return (
    <div
      className="flex flex-col gap-3 p-1"
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <RadioGroup
        value={link.targetType}
        onValueChange={(v) => handleTargetTypeChange(v as ButtonLinkTarget)}
        className="flex flex-col gap-2"
      >
        {targetOptions.map((opt) => {
          const id = `${uid}-${opt.value}`;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-2"
              onPointerDown={(e) => {
                // Avant le blur de l'input URL : sinon le re-render avale le clic
                // et on reste coincé sur « Lien externe ».
                e.preventDefault();
                handleTargetTypeChange(opt.value);
              }}
            >
              <RadioGroupItem value={opt.value} id={id} />
              <span className="text-sm font-medium leading-none">
                {opt.label}
              </span>
            </label>
          );
        })}
      </RadioGroup>

      {link.targetType === "NONE" ? null : link.targetType === "SYSTEM" ? (
        <div className="flex flex-col gap-1.5">
          <Select
            value={link.systemKey ?? ""}
            onValueChange={(systemKey) => update({ systemKey })}
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue
                placeholder={cmsNew(
                  "cms.new.auto.placeholder.choisir_une_page_speciale",
                  "Choisir une page spéciale…",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {getSystemRoutes().map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">
            {cmsNew(
              "cms.new.fields.linkTarget.systemHint",
              "Pages du site non gérées par le CMS (authentification d'œuvres, abonnement, comptes…). Le lien suit automatiquement la langue.",
            )}
          </p>
        </div>
      ) : link.targetType === "PAGE" ? (
        <div className="flex flex-col gap-1.5">
          <Select
            value={link.pageId ?? ""}
            onValueChange={(pageId) => update({ pageId })}
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue
                placeholder={cmsNew(
                  "cms.new.auto.placeholder.choisir_une_page",
                  "Choisir une page…",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {pages.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name ||
                    cmsNew("cms.new.fields.linkTarget.noName", "(sans nom)")}
                  {p.status !== "PUBLISHED"
                    ? cmsNew(
                        "cms.new.fields.linkTarget.draftSuffix",
                        " — brouillon",
                      )
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">
            {cmsPhrase(cmsNew, "linkInternalPageHint")}
          </p>
        </div>
      ) : link.targetType === "CATALOG_DECOUPAGE" ? (
        <div className="flex flex-col gap-3">
          {fixedCatalogDecoupage ? (
            <p className="text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.link.decoupageClassicUrl",
                "Pointe vers /{langue}/{slug-page-présentation-CR}/{slug-découpage} — le slug CR vient de la page liée dans Parcours catalogue (Présentation). Puis /{id-œuvre} sur le listing.",
              )}
            </p>
          ) : (
            <>
              <DecoupagePickerField
                value={{
                  target: catalogDecoupage.target,
                  decoupageId: catalogDecoupage.decoupageId,
                }}
                onChange={(next) =>
                  updateCatalogDecoupage({
                    target: next.target,
                    decoupageId: next.decoupageId,
                  })
                }
              />
              <p className="text-xs text-slate-400">
                {cmsPhrase(cmsNew, "linkDecoupageHint")}
              </p>
              {catalogDecoupageLinkKey(catalogDecoupage) ? null : (
                <p className="text-xs text-amber-700">
                  {cmsPhrase(cmsNew, "linkChooseDecoupage")}
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <Input
          value={link.url ?? ""}
          placeholder={cmsNew(
            "cms.new.auto.placeholder.https_ou_chemin",
            "https://… ou /chemin",
          )}
          onChange={(e) => update({ url: e.target.value })}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
      )}

      {link.targetType === "NONE" ? null : (
        <div className="flex items-center justify-between gap-3 text-sm text-slate-600">
          <span>{cmsPhrase(cmsNew, "openInNewTab")}</span>
          <Switch
            checked={link.openInNew}
            onCheckedChange={(openInNew) => update({ openInNew })}
          />
        </div>
      )}

      {previewUrl ? (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-xs font-medium text-slate-500">
            {cmsNew("cms.new.auto.text.apercu_du_lien", "Aperçu du lien")}
          </p>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 flex items-center gap-1 break-all text-xs text-primary hover:underline"
          >
            <ExternalLink size={12} className="shrink-0" />
            {previewUrl}
          </a>
          <p className="mt-1 text-[11px] text-slate-400">
            {cmsPhrase(cmsNew, "linkPreviewLangHint")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
